import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { BACKEND_URL } from "../config/env";
import { useSession } from "../lib/auth-client";
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  BadgeCheckIcon,
  Flag,
  Loader2,
  MapPin,
  ShieldCheckIcon,
  Upload,
  X,
} from "lucide-react";
import { MediaManager } from "../components/editor/MediaManager";
import { toast } from "sonner";
import { ReportDialog } from "@/components/ui/report-dialog";

// new shape mirrored from updated server aggregated profile endpoint (user + profile only)
interface PublicProfileData {
  id: string;
  username: string; // raw username or display
  name?: string | null;
  image?: string | null;
  displayUsername?: string | null;
  tagline?: string | null;
  location?: string | null;
  website?: string | null;
  followersCount?: number | null;
  followingCount?: number | null;
  createdAt: string;
  updatedAt: string;
  verified?: boolean | null;
  isSiteAdmin?: boolean | null;
  isFollowing?: boolean; // added to avoid extra network call
}

const BASE = BACKEND_URL || "";

export const Route = createFileRoute("/profile/$username")({
  loader: async ({ params }: { params: { username?: string } }) => {
    const username = params.username;
    if (!username) throw new Error("failed to load profile");

    const res = await fetch(
      `${BASE}/api/profile/${encodeURIComponent(username)}`,
      {
        headers: { Accept: "application/json" },
        credentials: "include",
      }
    );
    if (!res.ok) {
      if (res.status === 404) throw new Error("profile not found");
      throw new Error("failed to load profile");
    }
    return (await res.json()) as PublicProfileData;
  },
  component: ProfilePage,
  errorComponent: ({ error }: { error: unknown }) => {
    const message =
      error instanceof Error ? error.message : "failed to load profile";
    const isNotFound = message === "profile not found";
    return (
      <div className="flex items-center justify-center min-h-svh bg-background px-6">
        <div className="text-center max-w-sm">
          <h1 className="text-2xl font-bold text-accent mb-2">
            {isNotFound ? "profile not found" : "something went wrong"}
          </h1>
          <p className="text-muted-foreground mb-4">
            {isNotFound
              ? "we couldn’t find a profile for that username."
              : "please try again in a moment."}
          </p>
          <Link to="/" className="text-accent hover:underline">
            {"← back to home"}
          </Link>
        </div>
      </div>
    );
  },
});

function linkifyTagline(tagline: string) {
  const parts: Array<React.ReactNode> = [];
  const regex = /([#@][a-zA-Z0-9_]{2,30})/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(tagline))) {
    if (match.index > lastIndex) {
      parts.push(tagline.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith("#")) {
      parts.push(
        <span
          key={parts.length}
          className="text-accent hover:underline cursor-pointer"
          aria-label={`hashtag ${token.slice(1)}`}
        >
          {token}
        </span>
      );
    } else if (token.startsWith("@")) {
      const user = token.slice(1);
      parts.push(
        <a
          key={parts.length}
          href={`/profile/${encodeURIComponent(user)}`}
          className="text-accent hover:underline"
        >
          {token}
        </a>
      );
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < tagline.length) parts.push(tagline.slice(lastIndex));
  return parts;
}

function ProfilePage() {
  const data = Route.useLoaderData() as PublicProfileData | undefined;
  const { data: session } = useSession();

  const [isFollowing, setIsFollowing] = useState<boolean | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const PAGE_SIZE = 9;
  const [followers, setFollowers] = useState<
    Array<{
      id: string;
      name: string | null;
      displayUsername: string | null;
      image: string | null;
      followedAt: string;
      verified: boolean;
      isSiteAdmin: boolean;
    }>
  >([]);
  // page index retained only for internal reference; removed pagination display
  const [page] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [followersError, setFollowersError] = useState<string | null>(null);
  const [hasMoreFollowers, setHasMoreFollowers] = useState(false);
  const [followersNotFound, setFollowersNotFound] = useState(false);

  const FOLLOWING_PAGE_SIZE = 9;
  const [following, setFollowing] = useState<
    Array<{
      id: string;
      name: string | null;
      displayUsername: string | null;
      image: string | null;
      followedAt: string;
      verified: boolean;
      isSiteAdmin: boolean;
    }>
  >([]);
  const [followingCursor, setFollowingCursor] = useState<string | null>(null);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [followingError, setFollowingError] = useState<string | null>(null);
  const [hasMoreFollowing, setHasMoreFollowing] = useState(false);
  const [followingNotFound, setFollowingNotFound] = useState(false);
  const followingSectionRef = useRef<HTMLDivElement | null>(null);
  const [followingActivated, setFollowingActivated] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false); // retained but now tied to dialogOpen
  const [formName, setFormName] = useState<string>(data?.name || "");
  const [formTagline, setFormTagline] = useState<string>(data?.tagline || "");
  const [formLocation, setFormLocation] = useState<string>(
    data?.location || ""
  );
  const [formWebsite, setFormWebsite] = useState<string>(data?.website || "");
  // claimed links (bios) for featured link selection
  const [claimedLinks, setClaimedLinks] = useState<string[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksError, setLinksError] = useState<string | null>(null);
  // image comes from account (user.image). we still track a local pending value for optimistic preview
  const [formImage, setFormImage] = useState<string>(data?.image || "");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [showMediaManager, setShowMediaManager] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  useEffect(() => {
    // sync form fields when data changes (navigated to different profile)
    setFormName(data?.name || "");
    setFormTagline(data?.tagline || "");
    setFormLocation(data?.location || "");
    setFormWebsite(data?.website || "");
    setFormImage(data?.image || "");
    setEditMode(false);
    setDialogOpen(false);
    setSaveError(null);
  }, [data?.id]);

  const viewingOwn = useMemo(
    () => !!session && !!data && session.user.id === data.id,
    [session, data]
  );

  useEffect(() => {
    if (!dialogOpen || !viewingOwn) return;
    let abort = false;
    (async () => {
      setLinksLoading(true);
      setLinksError(null);
      try {
        const res = await fetch(`${BASE}/api/bios`, { credentials: "include" });
        if (!res.ok) throw new Error("failed to load links");
        const json = await res.json();
        if (abort) return;
        const slugs = Array.isArray(json)
          ? json
              .filter(
                (b: any) =>
                  b && typeof b.id === "string" && !b.id.startsWith("u-")
              )
              .map((b: any) => b.id)
          : [];
        setClaimedLinks(slugs.sort());
      } catch (e: any) {
        if (!abort) setLinksError(e?.message || "failed to load links");
      } finally {
        if (!abort) setLinksLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [dialogOpen, viewingOwn]);

  const dirty = useMemo(() => {
    return (
      formName !== (data?.name || "") ||
      formTagline !== (data?.tagline || "") ||
      formLocation !== (data?.location || "") ||
      formWebsite !== (data?.website || "") ||
      formImage !== (data?.image || "")
    );
  }, [formName, formTagline, formLocation, formWebsite, formImage, data]);

  const canSave = dirty && !saveBusy;

  async function saveProfile() {
    if (!canSave) return;
    setSaveBusy(true);
    setSaveError(null);
    try {
      const payload: Record<string, string> = {};
      if (formName.trim() && formName.trim() !== (data?.name || ""))
        payload.name = formName.trim();
      if (formTagline.trim() !== (data?.tagline || ""))
        payload.tagline = formTagline.trim();
      if (formLocation.trim() !== (data?.location || ""))
        payload.location = formLocation.trim();
      if (formWebsite.trim() !== (data?.website || ""))
        payload.website = formWebsite.trim();
      // include avatar image if changed (empty string clears)
      if (formImage !== (data?.image || "")) {
        payload.image = formImage.trim();
      }
      if (Object.keys(payload).length === 0) {
        setSaveBusy(false);
        setDialogOpen(false);
        setEditMode(false);
        return;
      }
      const res = await fetch(`${BASE}/api/profile`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const txt = await res.text();
        setSaveError(txt || "failed to save");
        toast.error("failed to save profile");
      } else {
        const updated = await res.json();
        Object.assign(data as any, updated);
        setSaveError(null);
        toast.success("profile saved");
        setDialogOpen(false);
        setEditMode(false);
      }
    } catch (e: any) {
      setSaveError(e?.message || "failed to save");
      toast.error("save error");
    } finally {
      setSaveBusy(false);
    }
  }

  const display = data?.displayUsername || data?.username || "";

  const fetchFollowStatus = useCallback(async () => {
    if (!data || viewingOwn) return;
    if (typeof data.isFollowing === "boolean") {
      setIsFollowing(data.isFollowing);
    } else {
      setIsFollowing(null);
    }
  }, [data, viewingOwn]);

  const followersSectionRef = useRef<HTMLDivElement | null>(null);
  const [followersActivated, setFollowersActivated] = useState(false);

  const fetchFollowersPage = useCallback(
    async (pageIndex: number, reset = false) => {
      if (!data || !followersActivated) return;
      setFollowersLoading(true);
      setFollowersError(null);
      try {
        const limit = PAGE_SIZE;
        let qs = `limit=${limit}`;
        if (!reset && cursor) qs += `&cursor=${encodeURIComponent(cursor)}`;
        else if (pageIndex > 0) qs += `&offset=${pageIndex * limit}`; // fallback if cursor missing
        const res = await fetch(
          `${BASE}/api/users/${data.id}/followers?${qs}`,
          { credentials: "include" }
        );
        if (res.status === 404) {
          setFollowers([]);
          setFollowersNotFound(true);
          setHasMoreFollowers(false);
          return;
        }
        if (!res.ok) throw new Error("failed to load followers");
        const json = await res.json();
        const list = Array.isArray(json.followers) ? json.followers : [];
        const nextCur =
          typeof json.nextCursor === "string" ? json.nextCursor : null;
        setFollowers((prev) => (reset ? list : [...prev, ...list]));
        setFollowersNotFound(
          reset
            ? list.length === 0
            : followers.length === 0 && list.length === 0
        );
        setHasMoreFollowers(!!nextCur);
        setCursor(nextCur);
      } catch (e: any) {
        setFollowersError(e?.message || "failed to load followers");
      } finally {
        setFollowersLoading(false);
      }
    },
    [data, followersActivated, cursor]
  );

  useEffect(() => {
    if (data) {
      fetchFollowStatus();
    }
  }, [fetchFollowStatus]);

  useEffect(() => {
    if (!followersSectionRef.current || followersActivated) return;
    const el = followersSectionRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setFollowersActivated(true);
            break;
          }
        }
      },
      { rootMargin: "200px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [followersActivated]);

  useEffect(() => {
    if (!followingSectionRef.current || followingActivated) return;
    const el = followingSectionRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setFollowingActivated(true);
            break;
          }
        }
      },
      { rootMargin: "200px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [followingActivated]);

  const fetchFollowingPage = useCallback(
    async (reset = false) => {
      if (!data || !followingActivated) return;
      setFollowingLoading(true);
      setFollowingError(null);
      try {
        const limit = FOLLOWING_PAGE_SIZE;
        let qs = `limit=${limit}`;
        if (!reset && followingCursor)
          qs += `&cursor=${encodeURIComponent(followingCursor)}`;
        const res = await fetch(
          `${BASE}/api/users/${data.id}/following?${qs}`,
          { credentials: "include" }
        );
        if (res.status === 404) {
          setFollowing([]);
          setFollowingNotFound(true);
          setHasMoreFollowing(false);
          return;
        }
        if (!res.ok) throw new Error("failed to load following");
        const json = await res.json();
        const list = Array.isArray(json.following) ? json.following : [];
        const nextCur =
          typeof json.nextCursor === "string" ? json.nextCursor : null;
        setFollowing((prev) => (reset ? list : [...prev, ...list]));
        setFollowingNotFound(reset ? list.length === 0 : false);
        setHasMoreFollowing(!!nextCur);
        setFollowingCursor(nextCur);
      } catch (e: any) {
        setFollowingError(e?.message || "failed to load following");
      } finally {
        setFollowingLoading(false);
      }
    },
    [data, followingActivated, followingCursor]
  );

  useEffect(() => {
    if (
      data &&
      followingActivated &&
      following.length === 0 &&
      !followingLoading
    ) {
      fetchFollowingPage(true);
    }
  }, [data?.id, followingActivated]);

  useEffect(() => {
    if (
      data &&
      followersActivated &&
      followers.length === 0 &&
      !followersLoading
    ) {
      fetchFollowersPage(0, true);
    }
  }, [data?.id, followersActivated]);

  const toggleFollow = async () => {
    if (!data || viewingOwn || isBusy) return;
    const next = !isFollowing;
    setIsFollowing(next);
    setIsBusy(true);
    // optimistic follower count update
    const prevCount = data.followersCount || 0;
    if (next) {
      (data as any).followersCount = prevCount + 1;
    } else {
      (data as any).followersCount = Math.max(0, prevCount - 1);
    }
    try {
      let res: Response;
      if (next) {
        res = await fetch(`${BASE}/api/follow`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ userId: data.id }),
        });
      } else {
        res = await fetch(`${BASE}/api/follow/${data.id}`, {
          method: "DELETE",
          credentials: "include",
        });
      }

      if (!res.ok) {
        setIsFollowing(!next);
        (data as any).followersCount = prevCount;
        toast.error(next ? "failed to follow" : "failed to unfollow");
      } else {
        toast.success(next ? "followed" : "unfollowed");
      }
    } catch {
      setIsFollowing(!next);
      (data as any).followersCount = prevCount;
      toast.error("network error");
    } finally {
      setIsBusy(false);
    }
  };

  const taglineContent = useMemo(
    () => (data?.tagline ? linkifyTagline(data.tagline) : null),
    [data?.tagline]
  );

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const title = display ? `@${display}` : "profile";
  const description =
    data?.tagline || (display ? `profile of @${display}` : "profile");
  const ogImage = data?.image || `${origin}/icon.png`;

  const helmet = (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="profile" />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );

  const totalFollowerPages = useMemo(() => {
    if (!data || data.followersCount == null) return 1;
    const raw = Math.ceil((data.followersCount || 0) / PAGE_SIZE);
    return Math.max(raw, 1);
  }, [data, PAGE_SIZE]);

  // skeleton state if data not loaded (router supplies data so minimal)
  if (!data) {
    return (
      <div className="min-h-svh flex items-center justify-center bg-background">
        <div className="animate-pulse w-full max-w-md mx-auto p-10 rounded-3xl bg-card/60 backdrop-blur border border-border shadow-xl space-y-6">
          <div className="flex gap-5 items-center">
            <div className="w-24 h-24 rounded-full bg-border/50" />
            <div className="flex-1 space-y-3">
              <div className="h-6 w-40 rounded bg-border/50" />
              <div className="h-4 w-24 rounded bg-border/40" />
              <div className="h-4 w-56 rounded bg-border/40" />
            </div>
          </div>
          <div className="flex gap-6 pt-2">
            <div className="h-5 w-20 rounded bg-border/40" />
            <div className="h-5 w-20 rounded bg-border/40" />
            <div className="h-5 w-28 rounded bg-border/30" />
          </div>
          <div className="h-4 w-1/2 rounded bg-border/40" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-svh flex items-center justify-center bg-background px-4 py-12">
      {helmet}
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
        <div className="relative w-full">
          <div
            className="absolute -inset-0.5 rounded-3xl bg-gradient-to-br from-accent/30 via-primary/20 to-secondary/30 blur-xl opacity-40"
            aria-hidden="true"
          />
          <div className="relative rounded-3xl h-full bg-card/70 backdrop-blur-xl border border-border shadow-2xl shadow-accent/10 p-10 flex flex-col">
            <div className="flex flex-col items-center text-center gap-6 flex-1">
              {data.image ? (
                <img
                  src={data.image}
                  alt={display}
                  className="w-28 h-28 rounded-full object-cover border border-border shadow-md"
                  loading="lazy"
                />
              ) : (
                <div className="w-28 h-28 rounded-full border border-border bg-muted flex items-center justify-center text-2xl font-semibold shadow-md">
                  {display.slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="space-y-2">
                <div className="flex flex-row items-center justify-center gap-2">
                  <h1 className="text-3xl font-bold tracking-tight">
                    {display}
                  </h1>
                  {data.verified && (
                    <span title="Verified on clove.moe">
                      <BadgeCheckIcon className="w-5 h-5 text-accent" />
                    </span>
                  )}
                  {data.isSiteAdmin && (
                    <span title="Site Administrator">
                      <ShieldCheckIcon className="w-5 h-5 text-primary" />
                    </span>
                  )}
                  {!viewingOwn && session && (
                    <button
                      type="button"
                      onClick={() => setReportDialogOpen(true)}
                      className="ml-2 p-1.5 rounded-full hover:bg-accent/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                      title="Report this profile"
                    >
                      <Flag className="w-4 h-4 text-muted-foreground hover:text-accent" />
                    </button>
                  )}
                </div>
                {viewingOwn && (
                  <div className="flex flex-row justify-center gap-x-2">
                    {!editMode && (
                      <button
                        type="button"
                        onClick={() => {
                          setDialogOpen(true);
                          setEditMode(true);
                        }}
                        className="cursor-pointer mt-2 px-3 py-1.5 rounded-full text-xs font-medium border border-border bg-background/60 hover:bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                      >
                        edit profile
                      </button>
                    )}
                    <button
                      type="button"
                      className="cursor-pointer mt-2 px-3 py-1.5 rounded-full text-xs font-medium border border-border bg-accent hover:bg-background/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                      onClick={() => {
                        window.location.href = "/dashboard";
                      }}
                    >
                      go to dashboard
                    </button>
                  </div>
                )}
                {editMode && (
                  <p className="text-xs text-muted-foreground/70">
                    editing your profile
                  </p>
                )}
                {!editMode && data.tagline && (
                  <p className="text-sm leading-relaxed text-muted-foreground pt-2 break-words">
                    {taglineContent}
                  </p>
                )}
                {!editMode && data.location && (
                  <p className="text-xs text-muted-foreground/80 pt-2">
                    <MapPin size={16} className="inline-block mr-1 mb-0.5" />
                    {data.location}
                  </p>
                )}
                {!editMode && data.website && (
                  <p className="pt-3 text-xs">
                    {/^https?:\/\//i.test(data.website) ||
                    /\./.test(data.website) ? (
                      <a
                        href={
                          data.website.startsWith("http")
                            ? data.website
                            : `https://${data.website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent hover:underline break-all"
                      >
                        {data.website.replace(/^https?:\/\//, "")}
                      </a>
                    ) : (
                      <>
                        <a
                          href={`/${data.website}`}
                          className="text-accent hover:underline break-all"
                        >
                          /{data.website}
                        </a>
                      </>
                    )}
                  </p>
                )}
                {!editMode &&
                  !data.tagline &&
                  !data.location &&
                  !data.website && (
                    <p className="text-xs text-muted-foreground/70">
                      no additional profile info provided
                    </p>
                  )}
              </div>
              {!viewingOwn && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={toggleFollow}
                    disabled={isBusy || isFollowing == null}
                    className={`px-5 py-2 rounded-full text-sm font-medium border transition-colors shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 disabled:opacity-60 disabled:cursor-not-allowed ${
                      isFollowing
                        ? "bg-background/60 border-border hover:bg-background"
                        : "bg-accent text-accent-foreground border-accent/60 hover:bg-accent/80"
                    }`}
                  >
                    {isFollowing
                      ? isBusy
                        ? "unfollowing..."
                        : "unfollow"
                      : isBusy
                      ? "following..."
                      : "follow"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="relative w-full" ref={followersSectionRef}>
          <div
            className="absolute -inset-0.5 rounded-3xl bg-gradient-to-br from-primary/30 via-accent/20 to-secondary/30 blur-xl opacity-40"
            aria-hidden="true"
          />
          <div className="relative rounded-3xl h-full bg-card/70 backdrop-blur-xl border border-border shadow-2xl shadow-primary/10 p-8 flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold tracking-tight">
                followers
              </h2>
              <div className="text-xs text-muted-foreground">
                page {page + 1} / {totalFollowerPages}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-6 flex-1">
              {!followersActivated && !followersLoading && (
                <div className="col-span-3 text-center text-sm text-muted-foreground">
                  followers will load when you scroll here
                </div>
              )}
              {followersLoading && (
                <>
                  {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                    <div
                      key={i}
                      className="flex flex-col items-center gap-2 animate-pulse"
                    >
                      <div className="w-20 h-20 rounded-2xl bg-border/40" />
                      <div className="h-3 w-16 rounded bg-border/40" />
                    </div>
                  ))}
                </>
              )}
              {followersActivated && !followersLoading && followersError && (
                <div className="col-span-3 text-center text-sm text-accent">
                  {followersError}
                </div>
              )}
              {followersActivated &&
                !followersLoading &&
                !followersError &&
                followersNotFound && (
                  <div className="col-span-3 text-center text-sm text-muted-foreground">
                    no followers yet, maybe you could be the first?
                  </div>
                )}
              {followersActivated &&
                !followersLoading &&
                !followersError &&
                !followersNotFound &&
                followers.length === 0 && (
                  <div className="col-span-3 text-center text-sm text-muted-foreground">
                    no followers yet
                  </div>
                )}
              {followersActivated &&
                !followersLoading &&
                !followersError &&
                followers.length > 0 &&
                followers.map((f) => {
                  const uname = f.displayUsername || f.id.slice(0, 6);
                  return (
                    <a
                      key={f.id}
                      href={`/profile/${encodeURIComponent(
                        f.displayUsername || f.id
                      )}`}
                      className="group flex flex-col items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                    >
                      {f.image ? (
                        <img
                          src={f.image}
                          alt={uname}
                          className="w-20 h-20 rounded-2xl object-cover border border-border shadow-sm group-hover:scale-[1.03] transition-transform"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-2xl border border-border bg-muted flex items-center justify-center text-lg font-semibold group-hover:scale-[1.03] transition-transform">
                          {uname.slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="flex flex-row items-center justify-center gap-1">
                        <div className="text-xs font-medium text-foreground/90 group-hover:text-accent transition-colors truncate w-full text-center">
                          {uname}
                        </div>
                        {f.verified && (
                          <span title="Verified on clove.moe">
                            <BadgeCheckIcon className="w-3 h-3 text-accent" />
                          </span>
                        )}
                        {f.isSiteAdmin && (
                          <span title="Site Administrator">
                            <ShieldCheckIcon className="w-3 h-3 text-primary" />
                          </span>
                        )}
                      </div>
                    </a>
                  );
                })}
            </div>
            {followersActivated && hasMoreFollowers && (
              <div className="pt-6 flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => fetchFollowersPage(page + 1)}
                  disabled={followersLoading}
                  className="px-5 py-2 rounded-full text-xs font-medium border border-border bg-background/60 hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {followersLoading ? "loading…" : "load more"}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="relative w-full" ref={followingSectionRef}>
          <div
            className="absolute -inset-0.5 rounded-3xl bg-gradient-to-br from-secondary/30 via-accent/20 to-primary/30 blur-xl opacity-40"
            aria-hidden="true"
          />
          <div className="relative rounded-3xl h-full bg-card/70 backdrop-blur-xl border border-border shadow-2xl shadow-secondary/10 p-8 flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold tracking-tight">
                following
              </h2>
            </div>
            <div className="grid grid-cols-3 gap-6 flex-1">
              {!followingActivated && !followingLoading && (
                <div className="col-span-3 text-center text-sm text-muted-foreground">
                  following will load when you scroll here
                </div>
              )}
              {followingLoading && (
                <>
                  {Array.from({ length: FOLLOWING_PAGE_SIZE }).map((_, i) => (
                    <div
                      key={i}
                      className="flex flex-col items-center gap-2 animate-pulse"
                    >
                      <div className="w-20 h-20 rounded-2xl bg-border/40" />
                      <div className="h-3 w-16 rounded bg-border/40" />
                    </div>
                  ))}
                </>
              )}
              {followingActivated && !followingLoading && followingError && (
                <div className="col-span-3 text-center text-sm text-accent">
                  {followingError}
                </div>
              )}
              {followingActivated &&
                !followingLoading &&
                !followingError &&
                followingNotFound && (
                  <div className="col-span-3 text-center text-sm text-muted-foreground">
                    not following anyone yet
                  </div>
                )}
              {followingActivated &&
                !followingLoading &&
                !followingError &&
                following.length === 0 &&
                !followingNotFound && (
                  <div className="col-span-3 text-center text-sm text-muted-foreground">
                    not following anyone
                  </div>
                )}
              {followingActivated &&
                !followingLoading &&
                !followingError &&
                following.length > 0 &&
                following.map((f) => {
                  const uname = f.displayUsername || f.id.slice(0, 6);
                  return (
                    <a
                      key={f.id}
                      href={`/profile/${encodeURIComponent(
                        f.displayUsername || f.id
                      )}`}
                      className="group flex flex-col items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                    >
                      {f.image ? (
                        <img
                          src={f.image}
                          alt={uname}
                          className="w-20 h-20 rounded-2xl object-cover border border-border shadow-sm group-hover:scale-[1.03] transition-transform"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-2xl border border-border bg-muted flex items-center justify-center text-lg font-semibold group-hover:scale-[1.03] transition-transform">
                          {uname.slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="flex flex-row items-center justify-center gap-1">
                        <div className="text-xs font-medium text-foreground/90 group-hover:text-accent transition-colors truncate w-full text-center">
                          {uname}
                        </div>
                        {f.verified && (
                          <span title="Verified on clove.moe">
                            <BadgeCheckIcon className="w-3 h-3 text-accent" />
                          </span>
                        )}
                        {f.isSiteAdmin && (
                          <span title="Site Administrator">
                            <ShieldCheckIcon className="w-3 h-3 text-primary" />
                          </span>
                        )}
                      </div>
                    </a>
                  );
                })}
            </div>
            {followingActivated && hasMoreFollowing && (
              <div className="pt-6 flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => fetchFollowingPage(false)}
                  disabled={followingLoading}
                  className="px-5 py-2 rounded-full text-xs font-medium border border-border bg-background/60 hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {followingLoading ? "loading…" : "load more"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) {
            setEditMode(false);
          }
        }}
      >
        <DialogContent className="max-w-lg border border-border">
          <DialogHeader>
            <DialogTitle className="text-lg">edit profile</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              update your public profile information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-medium text-muted-foreground">
                display name
              </label>
              <input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                maxLength={80}
                className="w-full px-3 py-2 rounded-md bg-background/50 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
                placeholder="your name"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-medium text-muted-foreground flex items-center justify-between">
                tagline
                <span className="text-[10px] font-normal text-muted-foreground/70">
                  {160 - formTagline.length}
                </span>
              </label>
              <textarea
                value={formTagline}
                onChange={(e) => setFormTagline(e.target.value)}
                maxLength={160}
                rows={3}
                className="w-full px-3 py-2 rounded-md bg-background/50 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-accent/40 resize-none"
                placeholder="a short bio or tagline"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-medium text-muted-foreground flex items-center justify-between">
                location
                <span className="text-[10px] font-normal text-muted-foreground/70">
                  {120 - formLocation.length}
                </span>
              </label>
              <input
                value={formLocation}
                onChange={(e) => setFormLocation(e.target.value)}
                maxLength={120}
                className="w-full px-3 py-2 rounded-md bg-background/50 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
                placeholder="where are you?"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-medium text-muted-foreground">
                featured link
              </label>
              {viewingOwn ? (
                <div className="flex flex-col gap-1">
                  <select
                    value={formWebsite}
                    onChange={(e) => setFormWebsite(e.target.value)}
                    className="w-full px-3 py-2 rounded-md bg-background/50 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
                  >
                    <option value="">none</option>
                    {claimedLinks.map((slug) => (
                      <option key={slug} value={slug}>
                        /{slug}
                      </option>
                    ))}
                  </select>
                  {linksLoading && (
                    <span className="text-[10px] text-muted-foreground">
                      loading links...
                    </span>
                  )}
                  {linksError && (
                    <span className="text-[10px] text-accent">
                      {linksError}
                    </span>
                  )}
                  <p className="text-[10px] text-muted-foreground/70">
                    pick one of your claimed links as your featured link
                    (appears on profile)
                  </p>
                </div>
              ) : (
                <input
                  value={formWebsite}
                  onChange={(e) => setFormWebsite(e.target.value)}
                  maxLength={200}
                  disabled
                  className="w-full px-3 py-2 rounded-md bg-background/50 border border-border text-sm opacity-60"
                  placeholder="no featured link"
                />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-medium text-muted-foreground">
                avatar
              </label>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full border border-border bg-muted overflow-hidden flex items-center justify-center">
                  {formImage ? (
                    <img
                      src={formImage}
                      alt="avatar preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">none</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploading}
                      className="rounded-full"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      <span className="ml-1">upload</span>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploading}
                      className="rounded-full"
                      onClick={() => setShowMediaManager(true)}
                    >
                      library
                    </Button>
                    {formImage && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={uploading}
                        className="rounded-full"
                        onClick={() => setFormImage("")}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground/70 max-w-xs">
                    jpg, png, webp, svg. max 50mb. avatar changes not saved
                    until you press save.
                  </p>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploading(true);
                  try {
                    const form = new FormData();
                    form.append("file", file);
                    const res = await fetch(`${BASE}/api/upload`, {
                      method: "POST",
                      credentials: "include",
                      body: form,
                    });
                    if (!res.ok) {
                      toast.error("upload failed");
                    } else {
                      const j = await res.json();
                      if (j?.url) {
                        setFormImage(j.url);
                        // defer persistence until explicit save so user can cancel
                        toast.success("avatar selected (not yet saved)");
                      } else {
                        toast.error("invalid upload response");
                      }
                    }
                  } catch {
                    toast.error("upload error");
                  } finally {
                    setUploading(false);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }
                }}
              />
            </div>
            {saveError && (
              <div className="text-xs text-accent/80 pt-1">{saveError}</div>
            )}
            <div className="flex items-center justify-between text-[10px] text-muted-foreground/70">
              <span>{dirty ? "unsaved changes" : "no changes"}</span>
            </div>
          </div>
          <DialogFooter className="flex items-center gap-3 justify-end">
            <button
              type="button"
              onClick={() => {
                setDialogOpen(false);
                setEditMode(false);
                setFormName(data?.name || "");
                setFormTagline(data?.tagline || "");
                setFormLocation(data?.location || "");
                setFormWebsite(data?.website || "");
                setFormImage(data?.image || "");
                setSaveError(null);
              }}
              className="px-4 py-2 rounded-full text-xs font-medium border border-border bg-background/60 hover:bg-background"
            >
              cancel
            </button>
            <button
              type="button"
              onClick={() => saveProfile()}
              disabled={!canSave}
              className="px-5 py-2 rounded-full text-xs font-medium border border-border bg-accent text-accent-foreground hover:bg-accent/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveBusy ? "saving..." : "save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MediaManager
        open={showMediaManager}
        onOpenChange={setShowMediaManager}
        onInsertUrl={(url) => {
          setFormImage(url);
          toast.success("avatar selected (not yet saved)");
        }}
      />
      {data && (
        <ReportDialog
          open={reportDialogOpen}
          onOpenChange={setReportDialogOpen}
          subject="user"
          reportedUsername={data.username}
          reportedUrl={window.location.href}
        />
      )}
    </div>
  );
}
// do not export the page component from route files to keep code-splitting optimal
